import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  message, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  InputNumber,
  Select,
  Divider,
  Alert,
  Spin,
  Tooltip
} from 'antd';
import {
  SettingOutlined,
  DownloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';

interface Plugin {
  name: string;
  displayName: string;
  type: 'framework' | 'plugin';
  description: string;
  icon: string;
  isInstalled: boolean;
  installedAt?: string;
  requiresFramework?: boolean;
  hasConfig?: boolean;
}

interface PluginManagementProps {
  serverId: string;
}

const PluginManagement: React.FC<PluginManagementProps> = ({ serverId }) => {
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [currentPlugin, setCurrentPlugin] = useState<Plugin | null>(null);
  const [config, setConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configForm] = Form.useForm();
  const [serverStatus, setServerStatus] = useState<string>('unknown');
  const [uninstallConfirmModalVisible, setUninstallConfirmModalVisible] = useState(false);
  const [pluginToUninstall, setPluginToUninstall] = useState<string>('');
  const [installConfirmModalVisible, setInstallConfirmModalVisible] = useState(false);
  const [pluginToInstall, setPluginToInstall] = useState<string>('');

  // 检查服务器状态
  const checkServerStatus = async () => {
    try {
      const response = await api.get(`/servers/${serverId}`);
      const status = response.data?.status;
      setServerStatus(status || 'unknown');
      return status;
    } catch (error) {
      console.error('检查服务器状态失败:', error);
      setServerStatus('unknown');
      return 'unknown';
    }
  };

  // 获取插件列表
  const fetchPlugins = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/plugins/${serverId}/plugins`);
      const serverPlugins = response.data.data?.plugins || [];
      
      // 添加自定义的悠悠鸟RCON插件
      const customPlugin: Plugin = {
        name: 'YouYouNiaoRCON',
        displayName: '悠悠鸟RCON',
        type: 'plugin',
        description: '悠悠鸟游戏专门对ARK控制台命令进行开发的控制台管理工具，支持服务器管理、玩家管理、物品管理、聊天系统、传送系统、销毁系统、部落管理、天气控制等功能。',
        icon: '🎮',
        isInstalled: false, // 这个需要根据实际安装状态来判断
        requiresFramework: true,
        hasConfig: false
      };
      
      // 分离框架和其他插件
      const frameworkPlugins = serverPlugins.filter((plugin: Plugin) => plugin.name === 'ARKserverAPI');
      const youYouNiaoPlugins = serverPlugins.filter((plugin: Plugin) => plugin.name === 'YouYouNiaoRCON');
      const otherPlugins = serverPlugins.filter((plugin: Plugin) => plugin.name !== 'ARKserverAPI' && plugin.name !== 'YouYouNiaoRCON');
      
      // 排序：框架第一，然后是悠悠鸟RCON，最后是其他插件
      const sortedPlugins = [
        ...frameworkPlugins,  // 框架插件始终在第一位
        ...youYouNiaoPlugins, // 悠悠鸟RCON插件（如果后端有的话）
        ...(youYouNiaoPlugins.length === 0 ? [customPlugin] : []), // 如果后端没有，则添加自定义的
        ...otherPlugins       // 其他插件
      ];
      
      setPlugins(sortedPlugins);
    } catch (error: any) {
      console.error('获取插件列表失败:', error);
      message.error(error.response?.data?.message || '获取插件列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlugins();
    checkServerStatus();
  }, [serverId]);

  // 安装插件前的检查
  const handleInstallClick = async (pluginName: string) => {
    // 先检查服务器状态
    const status = await checkServerStatus();
    
    if (status === 'running' || status === 'starting') {
      // 如果服务器正在运行或启动中，显示提示弹窗，不允许安装
      setPluginToInstall(pluginName);
      setInstallConfirmModalVisible(true);
    } else {
      // 如果服务器未运行，直接安装
      handleInstall(pluginName);
    }
  };

  // 确认安装插件
  const handleInstall = async (pluginName: string) => {
    try {
      setActionLoading(pluginName);
      
      if (pluginName === 'YouYouNiaoRCON') {
        // 悠悠鸟RCON插件的特殊安装逻辑
        await api.post(`/plugins/${serverId}/plugins/YouYouNiaoRCON/install`);
        message.success('悠悠鸟RCON插件安装成功');
      } else {
        // 普通插件的安装逻辑
        await api.post(`/plugins/${serverId}/plugins/${pluginName}/install`);
        message.success('插件安装成功');
      }
      
      fetchPlugins();
    } catch (error: any) {
      console.error('安装插件失败:', error);
      message.error(error.response?.data?.message || '安装插件失败');
    } finally {
      setActionLoading(null);
    }
  };

  // 卸载插件前的检查
  const handleUninstallClick = async (pluginName: string) => {
    // 先检查服务器状态
    const status = await checkServerStatus();
    
    if (status === 'running' || status === 'starting') {
      // 如果服务器正在运行或启动中，显示提示弹窗，不允许卸载
      setPluginToUninstall(pluginName);
      setUninstallConfirmModalVisible(true);
    } else {
      // 如果服务器未运行，直接卸载
      handleUninstall(pluginName);
    }
  };

  // 确认卸载插件
  const handleUninstall = async (pluginName: string) => {
    try {
      setActionLoading(pluginName);
      
      if (pluginName === 'YouYouNiaoRCON') {
        // 悠悠鸟RCON插件的特殊卸载逻辑
        await api.post(`/plugins/${serverId}/plugins/YouYouNiaoRCON/uninstall`);
        message.success('悠悠鸟RCON插件卸载成功');
      } else {
        // 普通插件的卸载逻辑
        await api.post(`/plugins/${serverId}/plugins/${pluginName}/uninstall`);
        message.success('插件卸载成功');
      }
      
      fetchPlugins();
    } catch (error: any) {
      console.error('卸载插件失败:', error);
      message.error(error.response?.data?.message || '卸载插件失败');
    } finally {
      setActionLoading(null);
    }
  };

  // 打开配置弹窗
  const handleConfig = async (plugin: Plugin) => {
    try {
      setConfigLoading(true);
      setCurrentPlugin(plugin);
      const response = await api.get(`/plugins/${serverId}/plugins/${plugin.name}/config`);
      setConfig(response.data.data?.config);
      configForm.setFieldsValue(response.data.data?.config);
      setConfigModalVisible(true);
    } catch (error: any) {
      console.error('获取插件配置失败:', error);
      message.error(error.response?.data?.message || '获取插件配置失败');
    } finally {
      setConfigLoading(false);
    }
  };

  // 保存配置
  const handleSaveConfig = async (values: any) => {
    try {
      setConfigLoading(true);
      await api.post(`/plugins/${serverId}/plugins/${currentPlugin?.name}/config`, {
        config: values
      });
      message.success('配置保存成功');
      setConfigModalVisible(false);
    } catch (error: any) {
      console.error('保存配置失败:', error);
      message.error(error.response?.data?.message || '保存配置失败');
    } finally {
      setConfigLoading(false);
    }
  };

  // 检查是否可以安装插件
  const canInstallPlugin = (plugin: Plugin) => {
    if (plugin.type === 'framework') return true;
    
    // 检查框架是否已安装
    const framework = plugins?.find(p => p.name === 'ARKserverAPI' && p.isInstalled);
    return !!framework;
  };

  // 获取插件状态标签
  const getPluginStatusTag = (plugin: Plugin) => {
    if (plugin.isInstalled) {
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          已安装
        </Tag>
      );
    } else if (canInstallPlugin(plugin)) {
      return (
        <Tag color="blue" icon={<DownloadOutlined />}>
          可安装
        </Tag>
      );
    } else {
      return (
        <Tag color="orange" icon={<InfoCircleOutlined />}>
          需要框架
        </Tag>
      );
    }
  };

  // 渲染配置表单
  const renderConfigForm = () => {
    if (!config) return null;

    return (
      <Form
        form={configForm}
        layout="vertical"
        onFinish={handleSaveConfig}
        initialValues={config}
      >
        {/* 启动消息配置 */}
        <Card title="启动消息配置" size="small" className="mb-4">
          <Form.Item name={['LunchMessage', 'enable']} label="启用启动消息" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name={['LunchMessage', 'message']} label="启动消息">
            <Input placeholder="服务器启动成功" />
          </Form.Item>
        </Card>

        {/* Socket配置 */}
        <Card title="Socket配置" size="small" className="mb-4">
          <Form.Item name={['Socket', 'map_name']} label="地图名称">
            <Input placeholder="孤岛" />
          </Form.Item>
          <Form.Item name={['Socket', 'port']} label="端口" extra="此字段为只读">
            <InputNumber disabled style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['Socket', 'url']} label="URL">
            <Input placeholder="127.0.0.1" />
          </Form.Item>
        </Card>

        {/* 聊天颜色配置 */}
        <Card title="聊天颜色配置" size="small" className="mb-4">
          <Form.Item name={['chatcolor', 'baistr']} label="白色">
            <Input placeholder='<RichColor Color="255, 255, 255, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'hongstr']} label="红色">
            <Input placeholder='<RichColor Color="255, 0, 0, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'jinstr']} label="金色">
            <Input placeholder='<RichColor Color="255, 255, 0, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'lanstr']} label="蓝色">
            <Input placeholder='<RichColor Color="0, 0, 255, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'lvstr']} label="绿色">
            <Input placeholder='<RichColor Color="0, 255, 0, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'qingstr']} label="青色">
            <Input placeholder='<RichColor Color="0, 255, 255, 255">{0}</>' />
          </Form.Item>
          <Form.Item name={['chatcolor', 'zistr']} label="紫色">
            <Input placeholder='<RichColor Color="255, 0, 255, 255">{0}</>' />
          </Form.Item>
        </Card>

        {/* 聊天样式配置 */}
        <Card title="聊天样式配置" size="small" className="mb-4">
          <Form.Item name={['chatstyle', 'Admins', 'iconpath']} label="管理员图标路径">
            <Input placeholder="/Game/PrimalEarth/CoreBlueprints/Items/Artifacts/Icons/HUD_TerranAlphaImplant_Icon.HUD_TerranAlphaImplant_Icon" />
          </Form.Item>
          <Form.Item name={['chatstyle', 'Admins', 'strcolor']} label="管理员文字颜色">
            <Select>
              <Select.Option value="baistr">白色</Select.Option>
              <Select.Option value="hongstr">红色</Select.Option>
              <Select.Option value="jinstr">金色</Select.Option>
              <Select.Option value="lanstr">蓝色</Select.Option>
              <Select.Option value="lvstr">绿色</Select.Option>
              <Select.Option value="qingstr">青色</Select.Option>
              <Select.Option value="zistr">紫色</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name={['chatstyle', 'Default', 'iconpath']} label="默认图标路径">
            <Input placeholder="/Game/PrimalEarth/CoreBlueprints/Items/Artifacts/Icons/Implant_Icon_Gamma_Remote.Implant_Icon_Gamma_Remote" />
          </Form.Item>
          <Form.Item name={['chatstyle', 'Default', 'strcolor']} label="默认文字颜色">
            <Select>
              <Select.Option value="baistr">白色</Select.Option>
              <Select.Option value="hongstr">红色</Select.Option>
              <Select.Option value="jinstr">金色</Select.Option>
              <Select.Option value="lanstr">蓝色</Select.Option>
              <Select.Option value="lvstr">绿色</Select.Option>
              <Select.Option value="qingstr">青色</Select.Option>
              <Select.Option value="zistr">紫色</Select.Option>
            </Select>
          </Form.Item>
        </Card>

        {/* 其他配置 */}
        <Card title="其他配置" size="small" className="mb-4">
          <Form.Item name="senderFormat" label="发送者格式">
            <Input placeholder="[ice_dt] ice_mz (ice_bl)<ice_vip>" />
          </Form.Item>
          <Form.Item name="showMap" label="显示地图" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showVip" label="显示VIP" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Card>
      </Form>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载插件列表中...</div>
      </div>
    );
  }

  return (
    <div className="plugin-management">
      {/* 框架安装提示 */}
      {!plugins?.find(p => p.name === 'ARKserverAPI' && p.isInstalled) && (
        <Alert
          message="请先安装ARK API框架"
          description="所有插件都需要先安装ARK API框架才能正常工作。"
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      {/* 插件列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plugins?.map((plugin) => (
          <Card
            key={plugin.name}
            title={
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{plugin.icon}</span>
                <span className="font-medium">{plugin.displayName}</span>
                {plugin.type === 'framework' && (
                  <Tag color="blue">框架</Tag>
                )}
              </div>
            }
            extra={getPluginStatusTag(plugin)}
            className="h-full flex flex-col"
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <div className="flex-1 space-y-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                {plugin.description}
              </p>
              
              {plugin.isInstalled && plugin.installedAt && (
                <div className="text-xs text-gray-500">
                  安装时间: {new Date(plugin.installedAt).toLocaleString()}
                </div>
              )}
              
              {!canInstallPlugin(plugin) && plugin.type === 'plugin' && (
                <div className="text-xs text-orange-500">
                  <InfoCircleOutlined className="mr-1" />
                  需要先安装ARK API框架
                </div>
              )}
            </div>
            
            {/* 底部按钮区域 */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex justify-end space-x-2">
                {plugin.isInstalled ? (
                  <>
                    {plugin.hasConfig && (
                      <Button
                        type="text"
                        icon={<SettingOutlined />}
                        onClick={() => handleConfig(plugin)}
                      >
                        配置
                      </Button>
                    )}
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={actionLoading === plugin.name}
                      onClick={() => handleUninstallClick(plugin.name)}
                    >
                      卸载
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={actionLoading === plugin.name}
                    disabled={!canInstallPlugin(plugin)}
                    onClick={() => handleInstallClick(plugin.name)}
                  >
                    安装
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* 配置弹窗 */}
      <Modal
        title={`配置 ${currentPlugin?.displayName}`}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setConfigModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={configLoading}
            onClick={() => configForm.submit()}
          >
            保存配置
          </Button>
        ]}
      >
        {configLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>加载配置中...</div>
          </div>
        ) : (
          renderConfigForm()
        )}
      </Modal>

      {/* 卸载提示弹窗 */}
      <Modal
        title="无法执行操作"
        open={uninstallConfirmModalVisible}
        onCancel={() => {
          setUninstallConfirmModalVisible(false);
          setPluginToUninstall('');
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setUninstallConfirmModalVisible(false);
              setPluginToUninstall('');
            }}
          >
            我知道了
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#ff4d4f' }}>
            服务器当前状态: {serverStatus}
          </div>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
            检测到服务器正在{serverStatus === 'running' ? '运行中' : '启动中'}，无法执行插件卸载操作。
            <br />
            <strong style={{ color: '#1890ff' }}>请先停止服务器再执行插件卸载操作</strong>，以确保操作安全。
          </div>
        </div>
      </Modal>

      {/* 安装提示弹窗 */}
      <Modal
        title="无法执行操作"
        open={installConfirmModalVisible}
        onCancel={() => {
          setInstallConfirmModalVisible(false);
          setPluginToInstall('');
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setInstallConfirmModalVisible(false);
              setPluginToInstall('');
            }}
          >
            我知道了
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#ff4d4f' }}>
            服务器当前状态: {serverStatus}
          </div>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
            检测到服务器正在{serverStatus === 'running' ? '运行中' : '启动中'}，无法执行插件安装操作。
            <br />
            <strong style={{ color: '#1890ff' }}>请先停止服务器再执行插件安装操作</strong>，以确保操作安全。
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PluginManagement;
